import { createProxyClient } from './create-proxy-client';
import { mapAccountInfo } from './map-account-info';
import { MsalModuleVersion } from '../static';
/**
 * Checks if a request is in MSAL v4 format.
 *
 * @param req - The request object to check
 * @returns True if the request is in v4 format (has a `request` property with `scopes` and `account`)
 */
function isRequestV4(req) {
    // Non-object/null values can never match the v4 request shape
    if (typeof req !== 'object' || req === null) {
        return false;
    }
    const requestV4 = req;
    return 'request' in requestV4;
}
/**
 * Creates a proxy provider for MSAL v2 compatibility.
 *
 * This function creates a Proxy that wraps the MSAL v4 provider and provides
 * v2-compatible method signatures and return types while using the latest
 * MSAL v4 implementation under the hood. The proxy handles type conversions
 * and method adaptations to maintain backward compatibility.
 *
 * @param provider - The base MSAL v4 provider instance to wrap
 * @returns A proxy provider implementing the v2-compatible interface
 *
 * @example
 * ```typescript
 * const baseProvider = new MsalProvider(config);
 * const v2Proxy = createProxyProvider(baseProvider);
 *
 * // Use v2-compatible API
 * await v2Proxy.login();
 * const token = await v2Proxy.acquireAccessToken({ scopes: ['User.Read'] });
 * ```
 */
export function createProxyProvider(provider) {
    // Create a v2-compatible client wrapper using the new client proxy
    const v2Client = createProxyClient(provider.client);
    // Use Proxy to intercept property access and provide v2-compatible implementations
    const proxy = new Proxy(provider, {
        get: (target, prop) => {
            // Adapt each v4 provider member to the v2-compatible shape expected by callers
            switch (prop) {
                case 'version': {
                    return provider.version;
                }
                case 'msalVersion': {
                    return MsalModuleVersion.V2;
                }
                case 'client': {
                    // Return the v2-compatible client wrapper
                    return v2Client;
                }
                case 'defaultClient': {
                    // Deprecated property - redirect to client with warning
                    console.warn('defaultClient is deprecated, use client instead');
                    // Same v2-compatible client wrapper as the `client` case above.
                    return v2Client;
                }
                case 'defaultAccount': {
                    // Map v4 account to v2 format for backward compatibility
                    const account = target.account;
                    const defaultAccount = account
                        ? mapAccountInfo(account)
                        : undefined;
                    return defaultAccount;
                }
                case 'defaultConfig': {
                    // Deprecated property - not available in v4
                    console.warn('defaultConfig is deprecated and not available in v4');
                    return undefined;
                }
                case 'createClient': {
                    // Deprecated method - return function that returns v2 client
                    console.warn('createClient is deprecated in MSAL v4');
                    const createClient = () => v2Client;
                    return createClient;
                }
                case 'acquireToken': {
                    // Adapt v4 acquireToken to v2 signature with proper type mapping
                    const acquireToken = async (req) => {
                        const args = isRequestV4(req)
                            ? req
                            : { request: { scopes: req.scopes, account: req.account } };
                        const result = await target.acquireToken(args);
                        // Convert null to undefined for v2 compatibility
                        return result || undefined;
                    };
                    return acquireToken;
                }
                case 'acquireAccessToken': {
                    // Adapt v4 acquireAccessToken to v2 signature
                    const acquireAccessToken = async (req) => {
                        const args = isRequestV4(req)
                            ? req
                            : { request: { scopes: req.scopes, account: req.account } };
                        return await target.acquireAccessToken(args);
                    };
                    return acquireAccessToken;
                }
                case 'login': {
                    // Adapt v4 login to v2 signature with optional parameters
                    const login = async (options) => {
                        // Skip login if already authenticated and onlyIfRequired is true
                        if (options?.onlyIfRequired && target.account) {
                            return;
                        }
                        // Call v4 login with empty scopes (v2 behavior)
                        await target.login({ request: { scopes: [] } });
                    };
                    return login;
                }
                case 'logout': {
                    // Adapt v4 logout to v2 signature
                    const logout = async (options) => {
                        await target.logout({ redirectUri: options?.redirectUri });
                    };
                    return logout;
                }
                case 'handleRedirect': {
                    // Adapt v4 handleRedirect to v2 signature
                    const handleRedirect = async () => {
                        await target.handleRedirect();
                        // v2 expects null after redirect handling
                        return null;
                    };
                    return handleRedirect;
                }
                case 'createProxyProvider': {
                    // Generic method to create proxy providers for different versions
                    const createProxyProvider = (version) => target.createProxyProvider(version);
                    return createProxyProvider;
                }
                case 'dispose': {
                    // No-op dispose method for v2 compatibility
                    return () => {
                        /** noop */
                    };
                }
                default: {
                    // The proxy is awaited in some contexts, so `then` must resolve to undefined
                    if (prop === 'then') {
                        return undefined;
                    }
                    // Exhaustive check to ensure all v2 properties are handled
                    const exhaustiveCheck = prop;
                    // Fallback: return original property from target for any unhandled cases
                    return target[exhaustiveCheck];
                }
            }
        },
    });
    // Return the proxy cast to v2 interface for type safety
    return proxy;
}
//# sourceMappingURL=create-proxy-provider.js.map